import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { templatesApi } from '../../services/api';
import { CUSTOM_RESPONSE_CATEGORY, FormTemplate } from '../../types';
import { errorMessage } from '../../utils/errors';
import ConfirmDialog from '../common/ConfirmDialog';

interface EditorState {
  id: number | null;
  question: string;
  value: string;
}

/** A prompt to open the editor with. `nonce` changes so the same prompt can be asked for twice. */
export interface CustomPrefill {
  question: string;
  nonce: number;
}

interface Props {
  notify: (message: string, severity?: 'success' | 'error') => void;
  /** Opens the "add" editor with this prompt filled in whenever it changes. */
  prefill?: CustomPrefill | null;
}

const CustomAnswersTab: React.FC<Props> = ({ notify, prefill = null }) => {
  const [items, setItems] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<FormTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await templatesApi.list(CUSTOM_RESPONSE_CATEGORY);
      // Defensive: only ever show custom responses here even if the server
      // ignores the category filter.
      setItems(data.filter((t) => t.category === CUSTOM_RESPONSE_CATEGORY));
    } catch (e) {
      setError(errorMessage(e, 'Could not load your custom answers.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (prefill) setEditor({ id: null, question: prefill.question, value: '' });
  }, [prefill]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (a) => a.question.toLowerCase().includes(q) || a.value.toLowerCase().includes(q)
    );
  }, [items, search]);

  const canSave = Boolean(editor && editor.question.trim() && editor.value.trim()) && !saving;

  const handleSave = async () => {
    if (!editor || !canSave) return;
    setSaving(true);
    try {
      if (editor.id === null) {
        const created = await templatesApi.create({
          category: CUSTOM_RESPONSE_CATEGORY,
          question: editor.question.trim(),
          value: editor.value.trim(),
        });
        // The backend upserts on (category, question); replace if it exists.
        setItems((prev) => {
          const without = prev.filter((t) => t.id !== created.id);
          return [created, ...without];
        });
        notify('Custom answer added.');
      } else {
        const updated = await templatesApi.patch(editor.id, {
          question: editor.question.trim(),
          value: editor.value.trim(),
          category: CUSTOM_RESPONSE_CATEGORY,
        });
        setItems((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
        notify('Custom answer updated.');
      }
      setEditor(null);
    } catch (e) {
      notify(errorMessage(e, 'Could not save the answer.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await templatesApi.remove(pendingDelete.id);
      setItems((prev) => prev.filter((t) => t.id !== pendingDelete.id));
      setPendingDelete(null);
      notify('Custom answer deleted.');
    } catch (e) {
      notify(errorMessage(e, 'Could not delete the answer.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Free-text answers for prompts that do not map to a standard question. The extension
        suggests these when an application asks something similar to the prompt you saved.
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          placeholder="Search prompts or answers"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setEditor({ id: null, question: '', value: '' })}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Add answer
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Card variant="outlined" sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              {items.length === 0 ? 'No custom answers yet' : 'No answers match your search'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {items.length === 0
                ? 'Add a prompt and the answer you want reused for it.'
                : 'Try clearing the search.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map((a) => (
            <Card key={a.id} variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {a.question}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {a.value}
                    </Typography>
                    {a.use_count > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Used {a.use_count} {a.use_count === 1 ? 'time' : 'times'}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => setEditor({ id: a.id, question: a.question, value: a.value })}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setPendingDelete(a)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={Boolean(editor)} onClose={() => !saving && setEditor(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editor?.id === null ? 'Add custom answer' : 'Edit custom answer'}</DialogTitle>
        <DialogContent>
          {editor && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Prompt"
                placeholder="e.g. What excites you about this team?"
                value={editor.question}
                onChange={(e) => setEditor((s) => (s ? { ...s, question: e.target.value } : s))}
                fullWidth
                // A prompt that arrived filled in only needs its answer.
                autoFocus={editor.id !== null || !editor.question}
                helperText="The application question this answer should match."
              />
              <TextField
                label="Answer"
                value={editor.value}
                onChange={(e) => setEditor((s) => (s ? { ...s, value: e.target.value } : s))}
                fullWidth
                multiline
                minRows={4}
                autoFocus={editor.id === null && Boolean(editor.question)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this custom answer?"
        message="The extension will no longer suggest it. This cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Box>
  );
};

export default CustomAnswersTab;
